import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';

const GroupRow = ({ group, level, expandedMap, onToggle, onSelectLeaf }) => {
  const hasChildren = (group.subgroups || []).length > 0;
  const isExpanded = expandedMap[group.keyPath];
  const statuses = Object.entries(group.statuses || {});

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack direction='row' alignItems='center' spacing={0.3} sx={{ pl: `${level * 12}px` }}>
            {hasChildren ? (
              <IconButton
                size='small'
                aria-label={isExpanded ? `Contraer ${group.keyPath}` : `Expandir ${group.keyPath}`}
                onClick={() => onToggle(group.keyPath)}
              >
                {isExpanded ? <ExpandMoreRoundedIcon fontSize='small' /> : <KeyboardArrowRightRoundedIcon fontSize='small' />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Typography sx={{ fontWeight: hasChildren ? 700 : 500 }} noWrap title={group.key}>
              {group.key}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align='right'>{group.count}</TableCell>
        <TableCell align='right'>{group.total_quantity}</TableCell>
        <TableCell>
          <Stack direction='row' gap={0.5} flexWrap='wrap'>
            {statuses.slice(0, 4).map(([status, quantity]) => (
              <Chip key={`${group.keyPath}-${status}`} size='small' label={`${status}: ${quantity}`} />
            ))}
            {statuses.length > 4 && <Chip size='small' variant='outlined' label={`+${statuses.length - 4}`} />}
          </Stack>
        </TableCell>
        <TableCell align='right'>
          {!hasChildren && (
            <Tooltip title='Ver ítems del grupo hoja'>
              <IconButton
                size='small'
                color='primary'
                aria-label={`Ver detalle ${group.keyPath}`}
                onClick={() => onSelectLeaf(group)}
              >
                <VisibilityRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      {hasChildren && isExpanded && group.subgroups.map((subgroup) => (
        <GroupRow
          key={subgroup.keyPath}
          group={subgroup}
          level={level + 1}
          expandedMap={expandedMap}
          onToggle={onToggle}
          onSelectLeaf={onSelectLeaf}
        />
      ))}
    </>
  );
};

const buildKeyPathTree = (groups = [], parentPath = '') => {
  return groups.map((group) => {
    const keyPath = parentPath ? `${parentPath} > ${group.key}` : group.key;
    return {
      ...group,
      keyPath,
      subgroups: buildKeyPathTree(group.subgroups || [], keyPath)
    };
  });
};

const GroupedTable = ({ groups = [], onSelectLeaf }) => {
  const [expandedMap, setExpandedMap] = useState({});

  const keyedGroups = useMemo(() => buildKeyPathTree(groups), [groups]);

  const toggleExpanded = (keyPath) => {
    setExpandedMap((prev) => ({
      ...prev,
      [keyPath]: !prev[keyPath]
    }));
  };

  if (!keyedGroups.length) {
    return (
      <Paper elevation={0} sx={{ p: 2.4, borderRadius: 2, border: '1px dashed #d4d4d8', textAlign: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>No hay grupos para mostrar en la tabla.</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Grupo</TableCell>
            <TableCell align='right'>Pedidos</TableCell>
            <TableCell align='right'>Unidades</TableCell>
            <TableCell>Estados</TableCell>
            <TableCell align='right'>Detalle</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keyedGroups.map((group) => (
            <GroupRow
              key={group.keyPath}
              group={group}
              level={0}
              expandedMap={expandedMap}
              onToggle={toggleExpanded}
              onSelectLeaf={onSelectLeaf}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GroupedTable;
